import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Copy, Check, AlertTriangle, Shield, AlertCircle, Lightbulb, RotateCcw } from 'lucide-react';
import { useState } from 'react';

function RiskBadge({ level }) {
  if (level === 'low') {
    return (
      <div className="flex items-center gap-3 risk-low card-soft">
        <Shield className="h-6 w-6" />
        <div>
          <p className="font-semibold">Low Risk</p>
          <p className="text-sm opacity-80">Generally safe to proceed</p>
        </div>
      </div>
    );
  }
  if (level === 'high') {
    return (
      <div className="flex items-center gap-3 risk-high card-soft">
        <AlertCircle className="h-6 w-6" />
        <div>
          <p className="font-semibold">High Risk</p>
          <p className="text-sm opacity-80">Consider carefully before proceeding</p>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3 risk-medium card-soft">
      <AlertTriangle className="h-6 w-6" />
      <div>
        <p className="font-semibold">Medium Risk</p>
        <p className="text-sm opacity-80">Proceed with caution</p>
      </div>
    </div>
  );
}

function TipsList({ tips }) {
  if (!tips || tips.length === 0) return null;
  
  return (
    <div className="mt-6 card-soft page-enter stagger-4">
      <h2 className="font-heading text-lg font-semibold text-foreground mb-3">
        Tips for Delivery
      </h2>
      <ul className="space-y-2 text-muted-foreground">
        <li className="flex items-start gap-2">
          <span className="text-primary">•</span>
          <span>{tips[0] || 'Stay calm and confident'}</span>
        </li>
        {tips[1] && (
          <li className="flex items-start gap-2">
            <span className="text-primary">•</span>
            <span>{tips[1]}</span>
          </li>
        )}
        {tips[2] && (
          <li className="flex items-start gap-2">
            <span className="text-primary">•</span>
            <span>{tips[2]}</span>
          </li>
        )}
      </ul>
    </div>
  );
}

export default function Results() {
  const location = useLocation();
  const navigate = useNavigate();
  const [copiedPrimary, setCopiedPrimary] = useState(false);
  const [copiedAssertive, setCopiedAssertive] = useState(false);

  const result = location.state?.result;

  if (!result) {
    return (
      <div className="min-h-screen pb-safe flex items-center justify-center">
        <div className="text-center px-6">
          <h2 className="font-heading text-xl font-semibold text-foreground">Session Not Found</h2>
          <p className="text-muted-foreground mt-2">Unable to load results for this session.</p>
          <Button onClick={() => navigate('/dashboard')} className="btn-primary mt-6">
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'primary') {
        setCopiedPrimary(true);
        setTimeout(() => setCopiedPrimary(false), 2000);
      } else {
        setCopiedAssertive(true);
        setTimeout(() => setCopiedAssertive(false), 2000);
      }
      toast.success('Copied to clipboard!');
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  return (
    <div className="min-h-screen pb-safe" data-testid="results-page">
      <div className="max-w-lg mx-auto px-6 pt-8">
        <button 
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          data-testid="back-to-dashboard-btn"
        >
          <ArrowLeft className="h-5 w-5" />
          <span>Dashboard</span>
        </button>

        <div className="mt-6 page-enter">
          <h1 className="font-heading text-3xl font-semibold text-foreground">Your Guidance</h1>
        </div>

        <div className="mt-6 page-enter stagger-1" data-testid="risk-level">
          <RiskBadge level={result.risk_level} />
        </div>

        <div className="mt-6 card-soft page-enter stagger-2">
          <h2 className="font-heading text-lg font-semibold text-foreground">Recommended Action</h2>
          <p className="mt-3 text-foreground leading-relaxed" data-testid="recommended-action">
            {result.recommended_action}
          </p>
        </div>

        <div className="mt-4 card-soft bg-secondary/50 page-enter stagger-2">
          <div className="flex items-start gap-3">
            <Lightbulb className="h-5 w-5 text-primary mt-0.5" />
            <div>
              <h3 className="font-semibold text-foreground">Why This Works</h3>
              <p className="mt-1 text-muted-foreground text-sm" data-testid="why-it-works">
                {result.why_it_works}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 card-soft page-enter stagger-3">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading text-lg font-semibold text-foreground">Your Script</h2>
            <Button
              data-testid="copy-primary-script-btn"
              variant="ghost"
              size="sm"
              onClick={() => copyToClipboard(result.primary_script, 'primary')}
              className="text-primary"
            >
              {copiedPrimary ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
              {copiedPrimary ? 'Copied!' : 'Copy'}
            </Button>
          </div>
          <div className="bg-muted rounded-xl p-4">
            <p className="text-foreground leading-relaxed italic" data-testid="primary-script">
              "{result.primary_script}"
            </p>
          </div>
        </div>

        <div className="mt-4 card-soft border-accent/30 page-enter stagger-3">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading text-lg font-semibold text-foreground">More Assertive Version</h2>
            <Button
              data-testid="copy-assertive-script-btn"
              variant="ghost"
              size="sm"
              onClick={() => copyToClipboard(result.assertive_script, 'assertive')}
              className="text-accent"
            >
              {copiedAssertive ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
              {copiedAssertive ? 'Copied!' : 'Copy'}
            </Button>
          </div>
          <div className="bg-accent/10 rounded-xl p-4">
            <p className="text-foreground leading-relaxed italic" data-testid="assertive-script">
              "{result.assertive_script}"
            </p>
          </div>
        </div>

        <TipsList tips={result.tips} />

        <div className="mt-4 card-soft bg-muted page-enter stagger-4">
          <h3 className="font-semibold text-foreground">About the Risk Level</h3>
          <p className="mt-2 text-sm text-muted-foreground" data-testid="risk-explanation">
            {result.risk_explanation}
          </p>
        </div>

        <div className="mt-8 space-y-3 page-enter stagger-4">
          <Button
            data-testid="new-scenario-btn"
            onClick={() => navigate('/dashboard')}
            className="btn-primary w-full"
          >
            <RotateCcw className="h-5 w-5 mr-2" />
            Try Another Scenario
          </Button>
        </div>

        <div className="mt-8 text-center pb-8">
          <p className="text-sm text-muted-foreground">
            Remember: You've got this. Practice makes perfect.
          </p>
        </div>
      </div>
    </div>
  );
}
